// client/src/pages/datasets/AddDataSet.js

import React, { useEffect, useState, useMemo } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { apiFetch } from "../../api/api";
import { toast } from "react-toastify";
import DataSetFieldRow from "./DataSetFieldRow";
import { confirmDialog } from '../../components/EveryIdDialogHost';

import {
  DndContext,
  closestCenter
} from "@dnd-kit/core";

import {
  SortableContext,
  verticalListSortingStrategy,
  arrayMove
} from "@dnd-kit/sortable";

import "../../styles/template.css";
import "../../styles/Buttons.css";
export default function AddDataSet() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const presetTemplateId = params.get("templateId") || "";

  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState(presetTemplateId);

  const [fields, setFields] = useState([]);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);


  // Load templates
  useEffect(() => {
    (async () => {
      try {
        const data = await apiFetch("/templates/gettemplates", {
          method: "POST",
          body: { cnt: 0, lmt: 500 },
        });

        const items = Array.isArray(data) ? data : data?.items || [];
        setTemplates(items);

        if (!presetTemplateId && items[0]?._id) {
          setTemplateId(items[0]._id);
        }
      } catch (e) {
        toast.error("Error loading templates");
      } finally {
        setLoading(false);
      }
    })();
  }, [presetTemplateId]);

  // Initialize dataset fields from template
  useEffect(() => {
    if (!templateId) return;

    const tpl = templates.find((t) => t._id === templateId);

    if (!tpl?.fields) {
      setFields([]);
      return;
    }

    const f = tpl.fields.map((field, idx) => ({
      key: field.key,
      label: field.label || field.key,
      type: field.type || "text",
      required: false,
      visible: true,
      order: idx + 1,
    }));

    setFields(f);
  }, [templateId, templates]);

  const handleTemplateChange = async (newTemplateId) => {
    if (fields.length > 0) {
      const ok = await confirmDialog(
        "Changing the template will reset all dataset fields. Continue?",
        { confirmLabel: 'Change Template' }
      );

      if (!ok) return;
    }

    setTemplateId(newTemplateId);
    setFields([]);
  };

  const updateField = (idx, prop, value) => {
    setFields((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [prop]: value };
      return copy;
    });
  };

  const handleDragEnd = (event) => {

    const { active, over } = event;

    if (!over || active.id === over.id) return;

    const oldIndex = fields.findIndex(f => f.key === active.id);
    const newIndex = fields.findIndex(f => f.key === over.id);

    const reordered = arrayMove(fields, oldIndex, newIndex)
      .map((f, i) => ({ ...f, order: i + 1 }));

    setFields(reordered);

  };


  const handleSave = async () => {
    if (!name || !templateId) {
      toast.error("Please provide a dataset name and template.");
      return;
    }

    try {
      setSaving(true);

      const body = {
        name,
        description,
        templateId,
        fields,
      };

      await apiFetch("/datasets/create", {
        method: "POST",
        body,
      });

      toast.success("Dataset created");
      navigate("/datasets");
    } catch (e) {
      toast.error(e?.response?.data?.message || e.message || "Create failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <div style={{ padding: 16 }}>Loading…</div>;
  }

  return (
    <div className="detail-page">
      <div
        className="detail-header"
        style={{ display: "flex", justifyContent: "space-between" }}
      >
        <h2>Add Project</h2>

        <div style={{ display: "flex", gap: 8 }}>
          <button
            className="btn-return"
            onClick={() => navigate("/datasets")}
          >
            Return
          </button>

          <button
            className="btn-green"
            disabled={saving}
            onClick={handleSave}
          >
            {saving ? "Saving…" : "Save Project"}
          </button>
        </div>
      </div>

      <div
        className="detail-card"
        style={{ background: "#eef2f7", padding: "16px 20px" }}
      >
        <div style={{ display: "grid", gap: 10 }}>

        <div>

          <label>Project Name</label>&nbsp;

          <input
            className="compact-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
          />&nbsp;&nbsp;&nbsp;&nbsp;
        </div>
        <div>

          <label>Description</label>&nbsp;
          <input
            className="compact-input"
            value={description}
            style={{ width: "80%" }}
            onChange={(e) => setDescription(e.target.value)}
          />

        </div>

        <div>

        </div>
        <div>
          <label><b>Data Fields</b></label>&nbsp;&nbsp;&nbsp;&nbsp;
          <label>Project Template</label>&nbsp;

          <select
            className="compact-input"
            value={templateId}
            onChange={(e) => handleTemplateChange(e.target.value)}
          >
            {templates.map((t) => (
              <option key={t._id} value={t._id}>
                {t.name || t._id}
              </option>
            ))}
          </select>


        </div>

        <DndContext
          collisionDetection={closestCenter}
          onDragEnd={handleDragEnd}
        >

          <SortableContext
            items={fields.map(f => f.key)}
            strategy={verticalListSortingStrategy}
          >

          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            <table className="compact-table">

              <thead>
                <tr>
                  <th style={{ width: 32 }}></th>
                  <th style={{ width: 40 }}>#</th>
                  <th>Field</th>
                  <th style={{ textAlign: "center" }}>Type</th>
                  <th style={{ textAlign: "center" }}>Include</th>
                  <th style={{ textAlign: "center" }}>Visible</th>
                </tr>
              </thead>

              <tbody style={{ background: "#fff" }}>

              {fields.map((f, i) => (
                <DataSetFieldRow
                  key={f.key}
                  field={f}
                  index={i}
                  updateField={updateField}
                />
              ))}

              </tbody>

            </table>
          </div>

          </SortableContext>

        </DndContext>


        </div>
      </div>
    </div>
  );
}
